import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './WikiPage.css';

export default function WikiPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [articles,  setArticles]  = useState([]);
  const [article,   setArticle]   = useState(null);   // {slug, title, content}
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const [editing,      setEditing]      = useState(false);
  const [draftTitle,   setDraftTitle]   = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [saving,        setSaving]      = useState(false);

  const [creating,     setCreating]     = useState(false);
  const [newTitle,     setNewTitle]     = useState('');

  const loadArticles = useCallback(() => {
    axios.get('/api/wiki/articles').then(r => {
      setArticles(r.data);
      if (!slug && r.data.length > 0) {
        navigate(`/wiki/${r.data[0].slug}`, { replace: true });
      }
    }).catch(() => {});
  }, [slug, navigate]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  useEffect(() => {
    if (!slug) { setArticle(null); return; }
    setLoading(true);
    setError(null);
    setEditing(false);
    axios.get(`/api/wiki/articles/${encodeURIComponent(slug)}`)
      .then(r => setArticle(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  function startEdit() {
    if (!article) return;
    setDraftTitle(article.title);
    setDraftContent(article.content);
    setEditing(true);
  }

  function saveEdit() {
    if (!article) return;
    setSaving(true);
    let content = draftContent;
    // Keep the leading "# Title" line in sync with the title field.
    const lines = content.split('\n');
    if (lines[0]?.trim().startsWith('# ')) {
      lines[0] = `# ${draftTitle}`;
    } else {
      lines.unshift(`# ${draftTitle}`, '');
    }
    content = lines.join('\n');

    axios.put(`/api/wiki/articles/${encodeURIComponent(article.slug)}`, { content })
      .then(r => { setArticle(r.data); setEditing(false); loadArticles(); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setSaving(false));
  }

  function deleteArticle() {
    if (!article) return;
    if (!window.confirm(`Delete "${article.title}"? This can't be undone.`)) return;
    axios.delete(`/api/wiki/articles/${encodeURIComponent(article.slug)}`)
      .then(() => {
        const remaining = articles.filter(a => a.slug !== article.slug);
        setArticles(remaining);
        navigate(remaining.length > 0 ? `/wiki/${remaining[0].slug}` : '/wiki');
      })
      .catch(e => setError(e.response?.data?.error || e.message));
  }

  function createArticle(e) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    axios.post('/api/wiki/articles', { title: newTitle.trim() })
      .then(r => {
        setNewTitle('');
        setCreating(false);
        loadArticles();
        navigate(`/wiki/${r.data.slug}`);
      })
      .catch(e => setError(e.response?.data?.error || e.message));
  }

  return (
    <div className="wiki-page">
      <aside className="wiki-sidebar">
        <div className="wiki-sidebar-header">
          <span>Articles</span>
          <button className="wiki-new-btn" onClick={() => setCreating(c => !c)}>+ New</button>
        </div>
        {creating && (
          <form className="wiki-new-form" onSubmit={createArticle}>
            <input
              className="wiki-new-input"
              placeholder="Article title…"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              autoFocus
            />
            <button type="submit" className="wiki-new-submit">Create</button>
          </form>
        )}
        <div className="wiki-article-list">
          {articles.map(a => (
            <div
              key={a.slug}
              className={`wiki-article-item ${a.slug === slug ? 'active' : ''}`}
              onClick={() => navigate(`/wiki/${a.slug}`)}
            >
              {a.title}
            </div>
          ))}
        </div>
      </aside>

      <main className="wiki-main">
        {error && <div className="wiki-error">{error}</div>}
        {loading && <div className="wiki-loading">Loading…</div>}

        {!loading && article && !editing && (
          <>
            <div className="wiki-toolbar">
              <button className="wiki-btn" onClick={startEdit}>✎ Edit</button>
              <button className="wiki-btn wiki-btn--danger" onClick={deleteArticle}>Delete</button>
            </div>
            <div className="wiki-content">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>
          </>
        )}

        {!loading && article && editing && (
          <div className="wiki-editor">
            <div className="wiki-toolbar">
              <input
                className="wiki-title-input"
                value={draftTitle}
                onChange={e => setDraftTitle(e.target.value)}
              />
              <button className="wiki-btn wiki-btn--primary" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="wiki-btn" onClick={() => setEditing(false)}>Cancel</button>
            </div>
            <textarea
              className="wiki-textarea"
              value={draftContent}
              onChange={e => setDraftContent(e.target.value)}
              spellCheck={false}
            />
          </div>
        )}

        {!loading && !article && articles.length === 0 && (
          <div className="wiki-empty">No articles yet — create one to get started.</div>
        )}
      </main>
    </div>
  );
}
